import type { CodesDetailItemsModel } from '@/api/model/codesModel'
import type {
  FolderList,
  FolderItem,
  FolderTree,
  FolderTreeItem,
} from '@/api/model/folderModel'
import { CodesEnum, LinkOpenTargetEnum } from '@/enums'
import type { ErrorMessage } from '@/error/error'

import { errorMessages } from '@/error/error'
import { useStorage } from '@/hooks/core'
import { parseJWT } from './jwt'
import regexpRules from './regexp'

export { regexpRules, parseJWT }

export {
  sortByDocumentTypeASC,
  sortByDocumentTypeDESC,
  sortByFileNameASC,
  sortByFileNameDESC,
  sortByFileNameASC_2,
  sortByFileNameDESC_2,
  sortByRevisionDateASC,
  sortByRevisionDateDESC,
  sortByRevisionDateASC_2,
  sortByRevisionDateDESC_2,
  sortByRegistrationDateASC,
  sortByRegistrationDateDESC,
  sortByInsertDateASC,
  sortByInsertDateDESC,
  sortByInsertPersonASC,
  sortByInsertPersonDESC,
} from './sort'

export {
  handleAPIException,
  handleBulkAPIException,
  formatAPIException,
  handleExceptionNormal,
} from './exception'

export {
  convertJSONToCSVFile,
  convertCSVFileToString,
  convertCSVFileToArray,
  appendEmptyCSVColumn,
} from './csv'

interface Parameters {
  [key: string]: string
}

/**
 * @description URL からパラメータを取得
 * @param key パラメータの鍵
 * @returns パラメータの値
 */
export const getSingleURLParameter = (key: string): string | null => {
  const url: URL = new URL(window.location.href)
  const parameters: Parameters = {}

  url.searchParams.forEach((key: string, value: string): void => {
    const _key = key as keyof Parameters
    parameters[_key] = value
  })

  return url.searchParams.get(key)
}

/**
 * @description 各種格式の日付をフォーマット
 * @param date
 * @returns `yyyy/mm/dd`
 */
export const formatDate = (date: string | Date | number): string => {
  if (!date) {
    return ''
  }

  const _date = new Date(date)
  const year = _date.getFullYear()
  const month = '0' + (_date.getMonth() + 1).toString()
  const day = '0' + _date.getDate().toString()

  return `${year}/${month.substring(month.length - 2, month.length)}/${day.substring(
    day.length - 2,
    day.length
  )}`
}

/**
 * ユニコード文字を含む文を Base64 にエンコード
 * @param text
 * @returns
 */
export function encodeUnicodeToBase64(text: string): string {
  return window.btoa(encodeURIComponent(text)) ?? ''
}

/**
 * ユニコード文字を含む文を Base64 からデコード
 * @param base64
 * @returns
 */
export function decodeUnicodeFromBase64(base64: string): string {
  return decodeURIComponent(window.atob(base64)) ?? ''
}

/**
 * @description ファイルを Base64 にエンコード
 * @param file
 * @returns Base64
 */
export async function encodeFileToBase64(
  file: File
): Promise<string | ArrayBuffer | null> {
  return new Promise((resovle, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resovle(reader.result)
    reader.onerror = (error) => reject(error)
  })
}

/**
 * @description Base64 にエンコードされたファイルから URL を作成
 * @param base64 Base64 文字列
 * @returns 作成した URL
 */
export async function getFileURLFromBase64(base64: string): Promise<string> {
  // base64 にエンコードされたファイルを blob に変換
  const base64Response: Response = await fetch(base64)
  const blob: Blob = await base64Response.blob()
  return window.URL.createObjectURL(blob)
}

/**
 * @descriurlption ファイルを開く若しくはダウンロード
 * @param config // { url, downloadName, target }
 */
export function openFile(config: {
  url: string
  downloadName?: string
  target?: LinkOpenTargetEnum
}) {
  // アンカーノードを作成
  const anchor: HTMLAnchorElement = document.createElement('a')

  // アンカー情報を設定
  anchor.href = config.url
  anchor.target = config.target ?? LinkOpenTargetEnum.SELF
  config.downloadName && (anchor.download = config.downloadName)

  // アンカーを描画して実行
  document.body.appendChild(anchor)
  anchor.click()

  // アンカーノードを廃棄
  document.body.removeChild(anchor)
}

/**
 * @description base64 にエンコードされたファイルを開く
 * @param base64 base64 文字列のファイル
 * @param fileName 表示されたいデフォルトのファイル名
 */
export async function openFileFromBase64(
  base64: string,
  fileName: string
): Promise<void> {
  // ファイルダウンロードの用意
  const url: string = await getFileURLFromBase64(base64)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  document.body.appendChild(a)

  // ファイルをダウンロードし一時の部品を取り除く
  a.click()
  window.URL.revokeObjectURL(url)
  document.body.removeChild(a)
}

/**
 * @description 別タブで PDF をプレイビューする
 * @param base64 base64 にエンコードされた PDF
 * @param openTarget 画面開く場所
 * @returns 開いた画面
 */
export async function openPDFOnBrowser(
  base64: string,
  openTarget: LinkOpenTargetEnum = LinkOpenTargetEnum.BLANK
): Promise<Window | null> {
  // NOTE: 臨時修正 grpc to pdf
  const text: string = base64.replace('application/grpc', 'application/pdf')
  const url: string = await getFileURLFromBase64(text)
  const newWindow = window.open(url, openTarget)
  return newWindow
}

// NOTE: 別のファイルで共通化したので、ここのは廃棄（使われているので削削しないで）
/**
 * エラーメッセージを取得
 * @param code error code
 * @param customeWords words for change
 * @returns message
 */
export const getErrorMessage = (code: string, customeWords?: string[]): string => {
  const messageItem: ErrorMessage | undefined = errorMessages.find(
    (item: ErrorMessage): boolean => item.code === code.toUpperCase()
  )

  if (!messageItem) {
    console.warn('メッセージの取得に失敗しました。')
    return ''
  }

  let message: string = messageItem.message

  customeWords?.forEach((word: string, index: number): void => {
    message = message.replace(`{${index}}`, word)
  })

  return message
}

/**
 * 商品フォルダ格式化メソッド
 * @param list バックエンドから取得したフォルダ一覧
 * @returns 画面描画用フォルダ一覧
 */
export function formatFolderList(list: FolderList): FolderTree {
  // Map でアイテムを保存
  const folderMap: Map<number, FolderItem> = new Map()

  list.forEach((item: FolderItem): void => {
    folderMap.set(item.folder_id, item)
  })

  // 一覧を樹形配列に更新
  const nestedList: FolderList = []

  list.forEach((item: FolderItem): void => {
    // 配列のメソッドでは新しいオブジェクトを返すのでオブジェクトの引用を保存する Map を用いる
    const parent: FolderItem | undefined = folderMap.get(item.parent_folder_id)

    // 親のないアイテムはトップレベルのアイテム
    if (parent) {
      ;(parent.children ?? (parent.children = [])).push(item)
      return
    }

    nestedList.push(item)
  })

  // ネストがあるので再帰でフォーマット
  const formatter = (item: FolderItem): FolderTreeItem => ({
    id: item.folder_id,
    label: item.forder_name,
    folderType: item.folder_type,
    readFlag: item.read_flag,
    displayFlag: item.display_flag,
    publicCatrgory: item.public_category,
    parentFolderId: item.parent_folder_id,
    folderInsertDatetime: formatDate(item.folder_insert_datetime),
    checkbox: false,
    editor: false,
    modifyStatus: 0,
    associatedStates: false,
    errorText: '',
    addButton: true,
    children: item.children?.map(formatter) ?? [],
  })

  return nestedList.map<FolderTreeItem>(formatter)
}

/**
 * @description オブジェクトが空であるかをチェック
 * @param data 任意のオブジェクト
 * @returns
 */
export const isObjectEmpty = (data: { [property: string]: any }): boolean => {
  return JSON.stringify(data) === '{}'
}

/**
 * @description 文字列の中の ASCII 文字の配列を戻す
 * @param text 任意の文字列
 * @returns ASCII 文字の配列
 */
export const getASCIILetterList = (text: string): string[] => {
  const MAX_ASCII_CODE = 127

  return text
    .toString()
    .split('')
    .filter((letter: string) => letter.charCodeAt(0) <= MAX_ASCII_CODE)
}

/**
 * @description 深いコピー
 * @param item
 * @returns
 */
export const deepCopy = <T>(item: T): T => {
  return JSON.parse(JSON.stringify(item ?? {}))
}

/**
 * @description 文字列のブール値をブール値に変換
 * @param data
 * @returns
 */
export const toBoolean = (data: string | boolean): boolean => {
  return data.toString().toLowerCase() === 'true'
}

/**
 * @description スリープ
 * @param milisecond スリープ時間（ミリ秒）
 * @returns
 */
export const sleep = async (milisecond?: number): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(() => resolve(), milisecond)
  })
}

/**
 * @description コード一覧から対象コードのアイテムを探す
 * @param codeID コードの管理番号
 * @param codeValue 対象コードのアイテムの値
 */
export const findCodeTarget = (
  codeID: CodesEnum,
  codeValue: string
): CodesDetailItemsModel | undefined => {
  const { loadCodesInformation } = useStorage()
  const codes = loadCodesInformation()

  if (!codes) {
    console.error(new Error('There is no codes.'))
    return
  }

  const targetCode = codes.find((item) => item.management_number === codeID)

  if (!targetCode) {
    console.error(new Error('Cannot find target code. '))
    return
  }

  return targetCode.item.find((item) => item.value === codeValue)
}
